import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { topicService } from "../../services/topicService";
import { useBookmarkStore } from "../../stores/bookmarkStore";
import { useHighlightStore } from "../../stores/highlightStore";
import "../../styles/BookmarkAnnotationPanel.css";

const BookmarkAnnotationPanel = ({ onNavigateToPage, onClose, topics, courseData, onSyncHighlights }) => {
  const [activeTab, setActiveTab] = useState("bookmarks");
  const [isLoading, setIsLoading] = useState(false);
  const [highlightTexts, setHighlightTexts] = useState({});
  const topicCache = useRef({});
  const loadedHighlightsRef = useRef(new Set());

  // Store hooks
  const bookmarks = useBookmarkStore((state) => state.bookmarks);
  const removeBookmark = useBookmarkStore((state) => state.removeBookmark);
  const fetchBookmarksByCourseId = useBookmarkStore((state) => state.fetchBookmarksByCourseId);
  const clearBookmarks = useBookmarkStore((state) => state.clearBookmarks);
  const highlights = useHighlightStore((state) => state.highlights);
  const fetchHighlightsByCourse = useHighlightStore((state) => state.fetchHighlightsByCourse);

  // Fetch bookmarks when component mounts
  useEffect(() => {
    const loadBookmarks = async () => {
      if (courseData && courseData.id) {
        setIsLoading(true);
        try {
          clearBookmarks();
          await fetchBookmarksByCourseId(courseData.id);
        } catch (error) {
          console.error("Failed to load bookmarks:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadBookmarks();
  }, [courseData, fetchBookmarksByCourseId, clearBookmarks]);

  // Fetch highlights when component mounts
  useEffect(() => {
    const loadHighlights = async () => {
      if (courseData && courseData.id) {
        setIsLoading(true);
        try {
          await fetchHighlightsByCourse(courseData.id);
        } catch (error) {
          console.error("Error loading highlights:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadHighlights();
  }, [courseData, fetchHighlightsByCourse]);

  // Fetch highlight text
  const fetchHighlightText = useCallback(async (highlight) => {
    const cacheKey = `${highlight.topicId}-${highlight.pageId}-${highlight.id}`;
    if (loadedHighlightsRef.current.has(cacheKey)) {
      return highlightTexts[cacheKey] || 'Loading...';
    }
    try {
      const topicId = highlight.topicId;
      const pageId = highlight.pageId;
      let topicData = topicCache.current[topicId];
      if (!topicData) {
        const response = await topicService.getTopicById(topicId);
        if (!response?.data?.data) {
          throw new Error("Failed to fetch topic data");
        }
        topicData = response.data.data;
        topicCache.current[topicId] = topicData;
      }
      const resources = topicData.resources || [];
      let resource = resources.find((r) => r.id === pageId);
      if (!resource && pageId.startsWith("page-")) {
        const pageIndex = parseInt(pageId.replace("page-", ""), 10);
        resource = resources.find((r) => r.orderIndex === pageIndex);
      }
      if (!resource && highlight.resourceId) {
        resource = resources.find((r) => r.id === highlight.resourceId);
      }
      if (!resource || !resource.content) {
        throw new Error(`Resource content not found for pageId=${pageId}, resourceId=${highlight.resourceId}`);
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = resource.content;
      if (highlight.startMeta && highlight.endMeta) {
        if (
          highlight.startMeta.parentTagName &&
          typeof highlight.startMeta.parentIndex === "number"
        ) {
          const parentElements = tempDiv.getElementsByTagName(highlight.startMeta.parentTagName);
          if (parentElements && parentElements.length > highlight.startMeta.parentIndex) {
            const parentElement = parentElements[highlight.startMeta.parentIndex];
            if (parentElement) {
              const fullText = parentElement.textContent || "";
              const startOffset = highlight.startMeta.textOffset || 0;
              const endOffset = highlight.endMeta.textOffset || fullText.length;
              const extractedText = fullText.substring(startOffset, endOffset);
              if (extractedText.trim()) {
                return extractedText.trim();
              }
            }
          }
        }
      }
      if (highlight.id) {
        const highlightElement = tempDiv.querySelector(`[data-highlight-id="${highlight.id}"]`);
        if (highlightElement && highlightElement.textContent) {
          return highlightElement.textContent.trim();
        }
      }
      const colorName = highlight.colorId
        ? highlight.colorId.charAt(0).toUpperCase() + highlight.colorId.slice(1)
        : "Unknown";
      return `${colorName} highlighted text (content not available)`;
    } catch (error) {
      console.warn(`Failed to fetch highlight text for highlight ${highlight.id}:`, error);
      return `Highlighted text (extraction failed: ${error.message})`;
    }
  }, []);

  // Compute allHighlights
  const allHighlights = useMemo(() => {
    const highlightList = [];
    Object.keys(highlights).forEach((topicId) => {
      Object.keys(highlights[topicId] || {}).forEach((pageId) => {
        (highlights[topicId][pageId] || []).forEach((highlight) => {
          highlightList.push({
            ...highlight,
            topicId,
            pageId,
            type: "highlight",
          });
        });
      });
    });
    return highlightList.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [highlights]);

  // Load highlight texts
  useEffect(() => {
    let isMounted = true;
    if (activeTab === "annotations" && allHighlights.length > 0) {
      const loadHighlightTexts = async () => {
        setIsLoading(true);
        const texts = { ...highlightTexts };
        const newHighlights = allHighlights.filter(
          (highlight) => !loadedHighlightsRef.current.has(`${highlight.topicId}-${highlight.pageId}-${highlight.id}`)
        );
        for (const highlight of newHighlights) {
          const key = `${highlight.topicId}-${highlight.pageId}-${highlight.id}`;
          const text = await fetchHighlightText(highlight);
          if (isMounted) {
            texts[key] = text;
            loadedHighlightsRef.current.add(key);
          }
        }
        if (isMounted) {
          setHighlightTexts(texts);
          setIsLoading(false);
        }
      };
      loadHighlightTexts();
    }
    return () => {
      isMounted = false;
    };
  }, [activeTab, allHighlights, fetchHighlightText]);

  // Trigger sync on close
  const handleClose = useCallback(() => {
    if (onSyncHighlights) {
      onSyncHighlights();
    }
    onClose();
  }, [onClose, onSyncHighlights]);

  // Map highlights to display data
  const displayHighlights = useMemo(() => {
    return allHighlights.map((highlight) => ({
      ...highlight,
      displayText: highlightTexts[`${highlight.topicId}-${highlight.pageId}-${highlight.id}`] || "Loading...",
    }));
  }, [allHighlights, highlightTexts]);

  const handleNavigateToBookmark = useCallback(
    (bookmark) => {
      setIsLoading(true);
      try {
        if (onNavigateToPage) {
          const targetTopic = topics?.find((topic) => topic.id === bookmark.topicId);
          if (targetTopic && bookmark.resourceId) {
            const resourceIndex = targetTopic.resources.findIndex(
              (resource) => resource.id === bookmark.resourceId
            );
            onNavigateToPage(bookmark.topicId, resourceIndex);
          } else {
            const pageIndex = (bookmark.pageNumber || 1) - 1;
            onNavigateToPage(bookmark.topicId, pageIndex);
          }
          handleClose();
        }
      } catch (error) {
        console.error("Failed to navigate to bookmark:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [onNavigateToPage, handleClose, topics]
  );

  const handleNavigateToHighlight = useCallback(
    (highlight) => {
      setIsLoading(true);
      try {
        if (onNavigateToPage) {
          const pageIndex = highlight.pageId.startsWith("page-")
            ? parseInt(highlight.pageId.replace("page-", ""), 10)
            : highlight.pageId;
          onNavigateToPage(highlight.topicId, pageIndex, highlight);
          handleClose();
        }
      } catch (error) {
        console.error("Failed to navigate to highlight:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [onNavigateToPage, handleClose]
  );

  const handleDeleteBookmark = useCallback(
    async (bookmarkId, event) => {
      event.stopPropagation();
      if (window.confirm("Are you sure you want to delete this bookmark?")) {
        setIsLoading(true);
        try {
          await removeBookmark(bookmarkId);
        } catch (error) {
          console.error("Failed to delete bookmark:", error);
          alert("Failed to delete bookmark. Please try again.");
        } finally {
          setIsLoading(false);
        }
      }
    },
    [removeBookmark]
  );

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTopicName = (topicId) => {
    if (topics && topics.length > 0) {
      const topic = topics.find((t) => t.id === topicId || t.realTopicId === topicId);
      if (topic && topic.title) {
        return topic.title;
      }
    }
    return topicId
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getColorName = (colorId) => {
    const colorMap = {
      yellow: "Yellow",
      blue: "Blue",
      green: "Green",
      red: "Red",
      purple: "Purple",
      orange: "Orange",
    };
    return colorMap[colorId] || "Unknown";
  };

  return (
    <div className="bookmark-annotation-panel">
      <div className="panel-overlay" onClick={handleClose} />
      <div className="panel-container">
        <div className="panel-header">
          <h2 className="panel-title">
            <span className="panel-icon">📚</span>
            Bookmarks & Annotations
          </h2>
          <button className="close-btn" onClick={handleClose} title="Close">
            <span>✕</span>
          </button>
        </div>
        <div className="panel-tabs">
          <button
            className={`tab-btn ${activeTab === "bookmarks" ? "active" : ""}`}
            onClick={() => setActiveTab("bookmarks")}
          >
            <span className="tab-icon">🔖</span>
            <span className="tab-text">Bookmarks</span>
            <span className="tab-count">({bookmarks.length})</span>
          </button>
          <button
            className={`tab-btn ${activeTab === "annotations" ? "active" : ""}`}
            onClick={() => setActiveTab("annotations")}
          >
            <span className="tab-icon">🎨</span>
            <span className="tab-text">Highlights</span>
            <span className="tab-count">({allHighlights.length})</span>
          </button>
        </div>
        <div className="panel-content">
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner">Loading...</div>
            </div>
          )}
          {activeTab === "bookmarks" && (
            <div className="bookmarks-section">
              {bookmarks.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📖</div>
                  <h3 className="empty-title">No bookmarks yet</h3>
                  <p className="empty-description">
                    Use the bookmark button in the top-right corner of the Tools
                    Panel to save pages for quick access later.
                  </p>
                </div>
              ) : (
                <div className="bookmarks-list">
                  {bookmarks.map((bookmark) => (
                    <div key={bookmark.id} className="bookmark-item">
                      <div className="bookmark-content">
                        <h4 className="bookmark-title">{bookmark.title}</h4>
                        <div className="bookmark-meta">
                          <span className="bookmark-topic">
                            {formatTopicName(bookmark.topicId)}
                          </span>
                          <span className="bookmark-separator">•</span>
                          <span className="bookmark-page">
                            Page{" "}
                            {(() => {
                              const targetTopic = topics?.find(
                                (topic) => topic.id === bookmark.topicId
                              );
                              if (targetTopic && bookmark.resourceId) {
                                const targetResource = targetTopic.resources?.find(
                                  (resource) => resource.id === bookmark.resourceId
                                );
                                return targetResource?.orderIndex || bookmark.pageNumber;
                              }
                              return bookmark.pageNumber;
                            })()}
                          </span>
                          <span className="bookmark-separator">•</span>
                          <span className="bookmark-date">{formatDate(bookmark.createdAt)}</span>
                        </div>
                      </div>
                      <div className="bookmark-actions">
                        <button
                          className="action-btn navigate-btn"
                          onClick={() => handleNavigateToBookmark(bookmark)}
                          title="Go to page"
                          disabled={isLoading}
                        >
                          <span>📍</span>
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={(e) => handleDeleteBookmark(bookmark.id, e)}
                          title="Remove bookmark"
                          disabled={isLoading}
                        >
                          <span>🗑️</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === "annotations" && (
            <div className="annotations-section">
              {allHighlights.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🎨</div>
                  <h3 className="empty-title">No highlights yet</h3>
                  <p className="empty-description">
                    Start highlighting text in your course content to see your
                    saved highlights here. You can highlight text by selecting
                    it and choosing a color.
                  </p>
                </div>
              ) : (
                <div className="highlights-list">
                  {displayHighlights.map((highlight) => (
                    <div key={highlight.id} className="highlight-item">
                      <div className="highlight-content">
                        <div className="highlight-header">
                          <div className="highlight-color-indicator">
                            <span
                              className={`color-dot color-${highlight.colorId}`}
                              title={`${getColorName(highlight.colorId)} highlight`}
                            ></span>
                            <span className="color-label">{getColorName(highlight.colorId)}</span>
                          </div>
                          <div className="highlight-meta">
                            <span className="highlight-topic">
                              {formatTopicName(highlight.topicId)}
                            </span>
                            <span className="highlight-separator">•</span>
                            <span className="highlight-date">{formatDate(highlight.createdAt)}</span>
                          </div>
                        </div>
                        {highlight.displayText && (
                          <div className="highlight-text">
                            <span className="highlight-quote">"</span>
                            {highlight.displayText}
                            <span className="highlight-quote">"</span>
                          </div>
                        )}
                      </div>
                      <div className="highlight-actions">
                        <button
                          className="action-btn navigate-btn"
                          onClick={() => handleNavigateToHighlight(highlight)}
                          title="Go to highlighted content"
                          disabled={isLoading}
                        >
                          <span>📍</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookmarkAnnotationPanel;